(() => {
  if (!customElements.get('cq-quick-gallery')) customElements.define('cq-quick-gallery', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.track = this.querySelector('.cq-quick-slides');
      this.slides = [...this.querySelectorAll('.cq-quick-slide')];
      this.buttons = [...this.querySelectorAll('[data-gallery-index]')];
      this.index = 0;
      this.addEventListener('click', event => {
        const thumbnail = event.target.closest('[data-gallery-index]');
        if (thumbnail) this.show(Number(thumbnail.dataset.galleryIndex));
        else if (event.target.closest('[data-gallery-next]')) this.show(this.index + 1);
        else if (event.target.closest('[data-gallery-previous]')) this.show(this.index - 1);
      });
      this.track.addEventListener('keydown', event => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.show(this.index + (event.key === 'ArrowRight' ? 1 : -1));
        }
      });
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => {
          const rect = this.track.getBoundingClientRect();
          const distances = this.slides.map(slide => Math.abs(slide.getBoundingClientRect().left - rect.left));
          this.index = distances.indexOf(Math.min(...distances));
          this.updateButtons();
        });
      }, { passive: true });
    }
    show(index) {
      this.index = Math.max(0, Math.min(this.slides.length - 1, index));
      const slide = this.slides[this.index];
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      this.track.scrollTo({ left: this.index * this.track.clientWidth * (rtl ? -1 : 1), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      this.updateButtons();
    }
    showImage(id) {
      const index = this.slides.findIndex(slide => String(slide.dataset.imageId) === String(id));
      if (index >= 0) this.show(index);
    }
    updateButtons() {
      this.buttons.forEach((button, index) => button.setAttribute('aria-pressed', String(index === this.index)));
    }
  });

  if (typeof DrawerModal === 'function' && !customElements.get('quick-view-drawer')) customElements.define('quick-view-drawer', class extends DrawerModal {
    constructor() {
      super();
      this.cache = new Map();
    }
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.drawerContent = this.querySelector('.drawer-content-inner');
      this.drawerFooter = this.querySelector('.drawer-footer');
      this.addEventListener('cq:variant-change', event => {
        this.querySelector('cq-quick-gallery')?.showImage(event.detail.variant?.imageId);
      });
      this.addEventListener('cq:purchase-total', () => this.syncFooter());
      this.backdropHandler = event => { if (this.isOpen && event.target === document.body) this.close(); };
      document.addEventListener('click', this.backdropHandler);
      this.cartHandler = event => {
        if (event.detail?.context !== 'CatalogOptions-quickview') return;
        const cart = document.querySelector('cq-cart-drawer');
        if (!cart) return;
        const focusCart = () => {
          if (cart.isOpen && !cart.contains(document.activeElement)) cart.focusFirst?.();
        };
        const setOpener = event => { event.detail.opener = this.openedBy; };
        document.addEventListener('cart-drawer:before-open', setOpener, { once: true });
        cart.querySelector('.cq-cart-panel')?.addEventListener('transitionend', focusCart, { once: true });
        requestAnimationFrame(focusCart);
        setTimeout(() => { focusCart(); document.removeEventListener('cart-drawer:before-open', setOpener); }, 600);
      };
      document.addEventListener('cart:item-add', this.cartHandler);
      this.addEventListener('keydown', event => {
        if (event.key !== 'Tab' || !this.isOpen) return;
        const items = [...this.querySelectorAll('a[href],button:not(:disabled),input:not([type=hidden]):not(:disabled),select,textarea,[tabindex="0"]')].filter(el => el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');
        const first = items[0], last = items[items.length - 1];
        if (event.shiftKey && (document.activeElement === first || !items.includes(document.activeElement))) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      });
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.footerObserver?.disconnect();
      document.removeEventListener('click', this.backdropHandler);
      document.removeEventListener('cart:item-add', this.cartHandler);
    }
    open(opener) {
      // Catalog Quick View and Options share the native configure dialog. Bundle
      // subclasses keep their own drawer and staging contract.
      if (this.localName === 'quick-view-drawer') {
        document.dispatchEvent(new CustomEvent('cq:configure-open', { detail: { opener } }));
        return;
      }
      const card = opener.closest('.card-product, .product-row, .product-compare-product');
      const url = opener.dataset.productUrl || card?.querySelector('.js-product-link')?.getAttribute('href');
      if (!url) return;
      this.inert = false;
      this.setAttribute('aria-hidden', 'false');
      opener.setAttribute('aria-expanded', 'true');
      super.open(opener);
      this.productUrl = url;
      this.loadProduct(url);
    }
    close() {
      this.controller?.abort();
      const card = this.openedBy?.closest('.cq-product-card');
      card?.classList.add('cq-card-return-focus');
      this.openedBy?.setAttribute('aria-expanded', 'false');
      super.close();
      this.inert = true;
      this.setAttribute('aria-hidden', 'true');
      requestAnimationFrame(() => card?.classList.remove('cq-card-return-focus'));
    }
    async loadProduct(productUrl) {
      this.controller?.abort();
      const controller = this.controller = new AbortController();
      this.drawerContent.innerHTML = '<span class="spinner spinner-lg"></span>';
      this.drawerContent.setAttribute('aria-busy', 'true');
      this.footerObserver?.disconnect();
      this.drawerFooter.replaceChildren();
      const url = new URL(productUrl, location.origin);
      url.searchParams.set('section_id', 'quick-view-product');
      try {
        let text = this.cache.get(url.href);
        if (!text) {
          const response = await fetch(url, { signal: controller.signal });
          if (!response.ok) throw new Error('Quick view unavailable');
          text = await response.text();
          if (!text.includes('cq-quick-product')) throw new Error('Quick view missing');
          // Cache only the small section response; product-page scripts/schema never enter the drawer.
          this.cache.set(url.href, text);
        }
        if (controller !== this.controller || !this.isOpen) return;
        this.renderProduct(new DOMParser().parseFromString(text, 'text/html'));
      } catch (error) {
        if (error.name === 'AbortError') return;
        const link = document.createElement('a');
        link.href = productUrl;
        link.className = 'button button-secondary';
        link.textContent = window.productsStrings.viewFullDetails;
        this.drawerContent.replaceChildren(link);
      } finally {
        if (controller === this.controller) this.drawerContent.removeAttribute('aria-busy');
      }
    }
    syncFooter() {
      const options = this.querySelector('cq-product-options');
      const submit = options?.querySelector('[type="submit"]');
      const total = options?.querySelector('.cq-options-total');
      if (!submit || !total || !this.drawerFooter) return;
      let actions = this.drawerFooter.querySelector('.cq-quick-footer-actions');
      if (!actions) {
        actions = document.createElement('div');
        actions.className = 'cq-quick-footer-actions';
        actions.append(total.cloneNode(true), submit.cloneNode(true));
        const proxy = actions.querySelector('button');
        proxy.type = 'button';
        proxy.removeAttribute('name');
        proxy.removeAttribute('id');
        proxy.removeAttribute('form');
        // Keep the existing form and its app/cart handlers as the sole submit path.
        proxy.addEventListener('click', () => {
          if (!submit.disabled && submit.getAttribute('aria-disabled') !== 'true') options.form.requestSubmit(submit);
        });
        this.drawerFooter.replaceChildren(actions);
        options.classList.add('cq-quick-footer-ready');
        this.footerObserver?.disconnect();
        this.footerObserver = new MutationObserver(() => this.syncFooter());
        this.footerObserver.observe(submit, { attributes: true, childList: true, subtree: true, characterData: true });
      }
      actions.querySelector('[data-option-total]').textContent = total.querySelector('[data-option-total]').textContent;
      const proxy = actions.querySelector('button');
      proxy.disabled = submit.disabled;
      proxy.setAttribute('aria-disabled', submit.getAttribute('aria-disabled') || String(submit.disabled));
      proxy.className = submit.className;
      proxy.replaceChildren(...[...submit.childNodes].map(node => node.cloneNode(true)));
    }
    renderProduct(html) {
      const product = html.querySelector('.cq-quick-product');
      if (!product) return;
      this.drawerContent.replaceChildren(product.cloneNode(true));
      this.syncFooter();
      this.querySelector('.drawer-content').scrollTop = 0;
      trapFocus(this, this.contains(document.activeElement) ? document.activeElement : this.querySelector('.drawer-header'));
    }
  });
})();
